<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import type { CmsElementForm } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { customValidators } from "@/i18n/utils/i18n-validators";

const { required, minLength, email, requiredIf } = customValidators();

import {
  useCmsElementConfig,
  useNavigationContext,
  useSalutations,
  useShopwareContext,
} from "#imports";
import { computed, reactive, ref } from "vue";
import { defu } from "defu";

const props = defineProps<{
  content: CmsElementForm;
}>();

type Translations = {
  form: {
    salutation: string;
    salutationPlaceholder: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    subject: string;
    subjectPlaceholder: string;
    comment: string;
    commentPlaceholder: string;
    privacy: string;
    dataProtection: string;
    submit: string;
    messages: {
      contactFormSuccess: string;
    };
  };
};

let translations: Translations = {
  form: {
    salutation: "Salutation",
    salutationPlaceholder: "Enter salutation...",
    firstName: "First name",
    firstNamePlaceholder: "Enter first name...",
    lastName: "Last name",
    lastNamePlaceholder: "Enter last name...",
    email: "Email address",
    emailPlaceholder: "Enter email address...",
    phone: "Phone number",
    phonePlaceholder: "Enter phone number...",
    subject: "Subject",
    subjectPlaceholder: "Enter subject...",
    comment: "Comment",
    commentPlaceholder: "Enter comment...",
    privacy: "Privacy",
    dataProtection: "I have read the data protection information.",
    submit: "Submit",
    messages: {
      contactFormSuccess:
        "We have received your contact request and will process it as soon as possible.",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const loading = ref<boolean>();
const formSent = ref<boolean>(false);

type ErrorMessages = ApiClientError<{
  errors: ApiError[];
}>;

const errorMessages = ref<ErrorMessages[]>([]);

const { getSalutations } = useSalutations();
const { foreignKey } = useNavigationContext();
const { apiClient } = useShopwareContext();
const { getConfigValue } = useCmsElementConfig(props.content);

const getConfirmationText = computed(
  () =>
    getConfigValue("confirmationText") ??
    translations.form.messages.contactFormSuccess
);
const getFormTitle = computed(() => getConfigValue("title") || "Contact");
const state = reactive({
  company: "",
  salutationId: "",
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  comment: "",
  phone: "",
  checkbox: false,
});

const rules = computed(() => ({
  company: {
    required,
  },
  email: {
    required,
    email,
  },
  firstName: {
    required,
    minLength: minLength(3),
  },
  lastName: {
    required,
    minLength: minLength(3),
  },
  salutationId: {
    required,
  },
  phone: {
    required,
    minLength: minLength(3),
  },
  subject: {
    required,
    minLength: minLength(3),
  },
  comment: {
    required,
    minLength: minLength(10),
  },
  checkbox: {
    required,
    isTrue: (value: boolean) => value === true,
  },
}));

const { t } = useI18n();

const $v = useVuelidate(rules, state);
const invokeSubmit = async () => {
  state.comment += "\n" + t("form.company") + ": " + state.company;
  $v.value.$touch();
  const valid = await $v.value.$validate();
  if (valid) {
    loading.value = true;
    try {
      await apiClient.invoke("sendContactMail post /contact-form", {
        body: {
          ...state,
          navigationId: foreignKey.value,
        },
      });
      formSent.value = true;
    } catch (e) {
      if (e instanceof ApiClientError) {
        errorMessages.value = e.details.errors;
      }
    } finally {
      loading.value = false;
    }
  }
};
</script>
<template>
  <form
    class="w-[554px] flex flex-col max-w-full mx-auto relative test"
    @submit.prevent="invokeSubmit"
  >
    <div
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center z-10 bg-white/50"
    >
      <div
        class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-gray-500"
      />
    </div>
    <div class="ml-[-20px] mr-[-20px] md:ml-5 md:mr-5">
      <h2 class="text-center mb-10">
        {{ getFormTitle }}
      </h2>
      <p class="mb-10 text-[14px]">{{ $t("form.formSubHeading") }}</p>
    </div>
    <template v-if="!formSent">
      <div
        class="grid grid-cols-12 gap-5 ml-[-20px] mr-[-20px] md:ml-5 md:mr-5"
      >
        <div class="col-span-12">
          <input
            id="company-name"
            v-model="state.company"
            name="company-name"
            type="text"
            :placeholder="$t('form.company') + ' *'"
            autocomplete="company-name"
            class="appearance-none relative block w-full text-[14px] h-11 pt-[10px] py-2 px-5 border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
            :class="[
              $v.company.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-skin-primary focus:border-skin-primary',
            ]"
            @blur="$v.company.$touch()"
          />
          <span
            v-if="$v.company.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
          >
            {{ $v.company.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12">
          <!--<label for="salutation">{{ $t('form.salutation') }} *</label>-->
          <select
            id="salutation"
            v-model="state.salutationId"
            name="salutation"
            class="input-sty bg-no-repeat text-[14px] pt-[10px] h-11 bg-select-arrow w-full rounded-[3px] py-2 px-5 focus:ring-gray-300"
            :class="[
              $v.salutationId.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-[#D9D9D9] focus:border-skin-primary',
            ]"
            @blur="$v.salutationId.$touch()"
          >
            <option value="" disabled selected>
              {{ $t("form.salutation") }} *
            </option>
            <option
              v-for="salutation in getSalutations"
              :key="salutation.id"
              :value="salutation.id"
            >
              {{ salutation.displayName }}
            </option>
          </select>
          <span
            v-if="$v.salutationId.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
          >
            {{ $v.salutationId.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12 md:col-span-6">
          <!--<label for="first-name">{{ $t('form.firstName') }} *</label>-->
          <input
            id="first-name"
            v-model="state.firstName"
            name="first-name"
            type="text"
            :placeholder="$t('form.firstName') + ' *'"
            autocomplete="first-name"
            class="appearance-none relative block w-full text-[14px] h-11 pt-[10px] py-2 px-5 border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
            :class="[
              $v.firstName.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-skin-primary focus:border-skin-primary',
            ]"
            @blur="$v.firstName.$touch()"
          />
          <span
            v-if="$v.firstName.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
          >
            {{ $v.firstName.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12 md:col-span-6">
          <!--<label for="last-name">{{ $t('form.lastName') }} *</label>-->
          <input
            id="last-name"
            v-model="state.lastName"
            name="last-name"
            type="text"
            :placeholder="$t('form.lastName') + ' *'"
            autocomplete="last-name"
            class="appearance-none relative block w-full text-[14px] h-11 py-2 px-5 pt-[10px] border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
            :class="[
              $v.lastName.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-skin-primary focus:border-skin-primary',
            ]"
            @blur="$v.lastName.$touch()"
          />
          <span
            v-if="$v.lastName.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary pt-[10px] border-skin-primary"
          >
            {{ $v.lastName.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12 md:col-span-6">
          <!--<label for="email-address">{{ $t('form.email') }} *</label>-->
          <input
            id="email-address"
            v-model="state.email"
            name="email"
            type="email"
            :placeholder="$t('form.email') + ' *'"
            autocomplete="email"
            :class="[
              $v.email.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-skin-primary focus:border-skin-primary',
            ]"
            class="appearance-none relative block w-full text-[14px] h-11 py-2 px-5 border pt-[10px] placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
            @blur="$v.email.$touch()"
          />
          <span
            v-if="$v.email.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
          >
            {{ $v.email.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12 md:col-span-6">
          <!--<label for="phone">{{ $t('form.phone') }} *</label>-->
          <input
            id="phone"
            v-model="state.phone"
            name="phone"
            type="text"
            :placeholder="$t('form.phone') + ' *'"
            autocomplete="phone"
            class="appearance-none relative block w-full h-11 text-[14px] py-2 px-5 border pt-[10px] placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
            :class="[
              $v.phone.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-skin-primary focus:border-skin-primary',
            ]"
            @blur="$v.phone.$touch()"
          />
          <span
            v-if="$v.phone.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
          >
            {{ $v.phone.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12">
          <!--<label for="subject">{{ $t('form.subject') }} *</label>-->
          <input
            id="subject"
            v-model="state.subject"
            name="subject"
            type="text"
            :placeholder="$t('form.subject') + ' *'"
            autocomplete="subject"
            class="appearance-none relative block w-full h-11 text-[14px] py-2 pt-[10px] px-5 border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
            :class="[
              $v.subject.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-skin-primary focus:border-skin-primary',
            ]"
            @blur="$v.subject.$touch()"
          />
          <span
            v-if="$v.subject.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
          >
            {{ $v.subject.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12">
          <!--<label for="comment">{{ $t('form.comment') }} *</label>-->
          <textarea
            id="comment"
            v-model="state.comment"
            name="comment"
            type="text"
            :placeholder="$t('form.comment') + ' *'"
            autocomplete="comment"
            class="appearance-none relative block w-full py-2 text-[14px] px-5 pt-[10px] border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
            :class="[
              $v.comment.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-skin-primary focus:border-skin-primary',
            ]"
            rows="5"
            @blur="$v.comment.$touch()"
          />
          <span
            v-if="$v.comment.$error"
            class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
          >
            {{ $v.comment.$errors[0].$message }}
          </span>
        </div>
        <div class="col-span-12 mt-5">
          <label class="mb-5 block text-[14px]"
            >{{ $t("form.privacy") }} *</label
          >
          <div class="flex gap-3 items-center">
            <input
              id="privacy"
              v-model="state.checkbox"
              name="privacy"
              type="checkbox"
              class="focus:ring-[#009639] w-6 h-6 mt-[-6px] text-[14px] border text-skin-theme rounded-sm"
              :class="[
                $v.checkbox.$error ? 'border-red-600' : 'border-skin-primary',
              ]"
            />
            <div>
              <label
                class="text-[14px]"
                :class="[$v.checkbox.$error ? 'text-red-600' : '']"
                for="privacy"
                v-html="$t('checkout.termsAdnConditionsLabel')"
              >
              </label>
            </div>
          </div>
        </div>
      </div>
      <div
        class="mt-10 pt-10 border-t border-skin-primary ml-[-20px] mr-[-20px] md:ml-5 md:mr-5"
      >
        <p class="text-[14px] text-skin-secondary leading-5">
          {{ $t("form.formMandatoryContent") }}
        </p>
      </div>
      <div class="flex justify-end ml-[-20px] mr-[-20px] md:ml-5 md:mr-5">
        <button
          class="w-full text-center text-primary btn mt-10 relative group overflow-hidden"
          type="submit"
        >
          {{ $t("form.submit") }}
        </button>
      </div>
    </template>
    <template v-else>
      <p class="py-10 text-lg text-center">{{ getConfirmationText }}</p>
    </template>
  </form>
</template>
